import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { WizardContainer, WizardStep } from '../../components/ui/WizardStepper';
import { Card } from '../../components/ui';
import { api } from '../../lib/api';

// Import wizard steps
import { BasicInfoStep } from './wizard-steps/BasicInfoStep';
import { AuthConfigStep } from './wizard-steps/AuthConfigStep';
import { CredentialSchemaStep } from './wizard-steps/CredentialSchemaStep';
import { OperationMappingStep } from './wizard-steps/OperationMappingStep';
import { WebhookConfigStep } from './wizard-steps/WebhookConfigStep';
import { ReviewStep } from './wizard-steps/ReviewStep';

// Types
export interface AuthConfig {
  type: 'BEARER' | 'BASIC' | 'API_KEY' | 'OAUTH2' | 'CUSTOM_HEADER';
  headerName?: string;
  headerPrefix?: string;
  oauth2Config?: {
    tokenUrl: string;
    clientIdField: string;
    clientSecretField: string;
    scope?: string;
  };
}

export interface CredentialField {
  name: string;
  type: 'string' | 'password' | 'url' | 'number' | 'boolean';
  label: string;
  required: boolean;
  placeholder?: string;
  description?: string;
  validation?: {
    pattern?: string;
    patternMessage?: string;
    minLength?: number;
    maxLength?: number;
  };
}

export interface FieldMapping {
  sourcePath: string;
  targetPath: string;
  transformer?: {
    type: string;
    options?: Record<string, any>;
  };
}

export interface OperationMapping {
  name: string;
  description?: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  requestMapping: FieldMapping[];
  responseMapping: FieldMapping[];
  headers?: Record<string, string>;
}

export interface EventMapping {
  providerEvent: string;
  hubEvent: string;
  payloadMapping: FieldMapping[];
}

export interface WebhookConfig {
  enabled: boolean;
  signatureValidation: 'HMAC_SHA256' | 'HMAC_SHA1' | 'RSA' | 'NONE';
  signatureHeader: string;
  signatureSecretField?: string;
  eventMappings: EventMapping[];
}

export interface TemplateFormData {
  // Step 1: Basic Info
  name: string;
  type: 'REST' | 'GRAPHQL' | 'EVM' | 'SOLANA' | 'DEX';
  version: string;
  description: string;
  baseUrl: string;
  
  // Step 2: Authentication
  authConfig: AuthConfig;
  
  // Step 3: Credential Schema
  credentialSchema: CredentialField[];
  
  // Step 4: Operations
  operations: OperationMapping[];
  
  // Step 5: Webhooks
  webhookConfig: WebhookConfig;
}

const initialFormData: TemplateFormData = {
  name: '',
  type: 'REST',
  version: '1.0.0',
  description: '',
  baseUrl: '',
  authConfig: {
    type: 'BEARER',
    headerName: 'Authorization',
    headerPrefix: 'Bearer',
  },
  credentialSchema: [],
  operations: [],
  webhookConfig: {
    enabled: false,
    signatureValidation: 'HMAC_SHA256',
    signatureHeader: 'x-webhook-signature',
    eventMappings: [],
  },
};

const wizardSteps: WizardStep[] = [
  { id: 'basic', title: 'Informações Básicas', description: 'Nome, tipo e versão' },
  { id: 'auth', title: 'Autenticação', description: 'Configurar auth' },
  { id: 'credentials', title: 'Credenciais', description: 'Schema de campos' },
  { id: 'operations', title: 'Operações', description: 'Mapeamento de APIs' },
  { id: 'webhooks', title: 'Webhooks', description: 'Eventos e assinatura' },
  { id: 'review', title: 'Revisão', description: 'Confirmar e salvar' },
];

export const TemplateWizard: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const isEditing = !!templateId;

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>({});

  // Load existing template for editing
  const { data: templateData, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['provider-template', templateId],
    queryFn: async () => {
      const response = await api.get(`/provider-templates/${templateId}`);
      return response.data;
    },
    enabled: isEditing,
  });

  // Update form data when template loads
  useEffect(() => {
    if (templateData) {
      setFormData({
        name: templateData.name,
        type: templateData.type,
        version: templateData.version,
        description: templateData.description || '',
        baseUrl: templateData.config?.baseUrl || '',
        authConfig: templateData.config?.authConfig || initialFormData.authConfig,
        credentialSchema: templateData.config?.credentialSchema || [],
        operations: templateData.config?.operations || [],
        webhookConfig: templateData.config?.webhookConfig || initialFormData.webhookConfig,
      });
    }
  }, [templateData]);

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      const payload = {
        name: data.name,
        type: data.type,
        version: data.version,
        description: data.description,
        config: {
          baseUrl: data.baseUrl,
          authConfig: data.authConfig,
          credentialSchema: data.credentialSchema,
          operations: data.operations,
          webhookConfig: data.webhookConfig,
        },
      };

      if (isEditing) {
        return api.put(`/provider-templates/${templateId}`, payload);
      }
      return api.post('/provider-templates', payload);
    },
    onSuccess: () => {
      navigate('/admin/provider-templates');
    },
  });

  // Update form data for a specific step
  const updateFormData = (updates: Partial<TemplateFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Handle step validation - memoized to prevent infinite loops in child useEffects
  const handleStepValidation = useCallback((stepIndex: number, isValid: boolean) => {
    setStepValidation((prev) => {
      // Only update if the value actually changed
      if (prev[stepIndex] === isValid) {
        return prev;
      }
      return { ...prev, [stepIndex]: isValid };
    });
  }, []);

  // Check if current step is valid
  const isCurrentStepValid = stepValidation[currentStep] !== false;

  // Handle complete
  const handleComplete = () => {
    mutation.mutate(formData);
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/admin/provider-templates');
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInfoStep
            data={formData}
            onChange={updateFormData}
            onValidate={(isValid) => handleStepValidation(0, isValid)}
          />
        );
      case 1:
        return (
          <AuthConfigStep
            data={formData}
            onChange={updateFormData}
            onValidate={(isValid) => handleStepValidation(1, isValid)}
          />
        );
      case 2:
        return (
          <CredentialSchemaStep
            data={formData}
            onChange={updateFormData}
            onValidate={(isValid) => handleStepValidation(2, isValid)}
          />
        );
      case 3:
        return (
          <OperationMappingStep
            data={formData}
            onChange={updateFormData}
            onValidate={(isValid) => handleStepValidation(3, isValid)}
          />
        );
      case 4:
        return (
          <WebhookConfigStep
            data={formData}
            onChange={updateFormData}
            onValidate={(isValid) => handleStepValidation(4, isValid)}
          />
        );
      case 5:
        return (
          <ReviewStep
            data={formData}
            onValidate={(isValid) => handleStepValidation(5, isValid)}
          />
        );
      default:
        return null;
    }
  };

  if (isEditing && isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleCancel}
          className="p-2 text-surface-400 hover:text-white hover:bg-surface-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isEditing ? 'Editar Template' : 'Novo Template de Provider'}
          </h1>
          <p className="text-surface-400 mt-1">
            Configure um novo template de integração com provider
          </p>
        </div>
      </div>

      {/* Wizard */}
      <Card className="h-[calc(100vh-200px)] min-h-[600px]">
        <WizardContainer
          steps={wizardSteps}
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          onComplete={handleComplete}
          onCancel={handleCancel}
          isValid={isCurrentStepValid}
          isLoading={mutation.isPending}
          allowBackNavigation={true}
          completeButtonText={isEditing ? 'Salvar Alterações' : 'Criar Template'}
        >
          {renderStepContent()}
        </WizardContainer>
      </Card>

      {/* Error Message */}
      {mutation.isError && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">
            Erro ao salvar template: {(mutation.error as any)?.message || 'Erro desconhecido'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TemplateWizard;
